import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import ProtectedRoute from '../components/auth/ProtectedRoute'
import { MembershipCard, AppHeader } from '../components'
import { ArrowLeft } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'

export default function MembershipPage() {
  const { user } = useAuth()
  const [error, setError] = useState<string | null>(null)

  // ProtectedRoute ensures user is not null, but TypeScript doesn't know that
  if (!user) {
    return null
  }

  return (
    <ProtectedRoute>
      <Helmet>
        <title>Pilot ID Card - FWO Pilot Portal</title>
        <meta name="description" content="Your FlyWideOpen digital pilot identification card" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Header */}
        <AppHeader activePage="membership" />

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Link 
              to="/dashboard"
              className="inline-flex items-center text-fwo-blue hover:text-blue-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Link>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Pilot ID Card
            </h2>
            <p className="text-gray-600">
              Your digital FlyWideOpen pilot identification card
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Membership Card Component */}
          <MembershipCard onError={setError} />

          {/* Information Section */}
          <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">About Your Pilot ID Card</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Digital Wallet</h4>
                <p>
                  Add your card to Apple Wallet or Google Wallet for quick access on your phone. 
                  Perfect for showing at competition check-ins and events.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">QR Code Verification</h4>
                <p>
                  The QR code allows event staff to quickly verify your pilot status and access 
                  your emergency contact information if needed.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Download Options</h4>
                <p>
                  Download as PNG for easy sharing or SVG for high-quality printing. 
                  Both formats include all your pilot information and the verification QR code.
                </p>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Important Contacts</h4>
                <p>
                  Your card includes essential competition contacts and retrieve instructions. 
                  Keep it accessible during flying activities.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}